import { Box, Grid, Card, Typography, Button, ButtonGroup, TextField, Popover, CircularProgress } from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Paper } from "@mui/material";
import axios from 'axios';
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Link as RouterLink } from "react-router-dom";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import Header from "../../components/Header";
import { Bar, Doughnut } from 'react-chartjs-2';
import { useBrand } from "../../contexts/BrandContext";
import { api } from "../../services/auth";
import segmentService from "../../services/segmentService";
import { loyaltyFetch, LOYALTY_API_BASE } from '../fidelite/hooks/loyaltyFetch';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Custom number formatter that uses truncation instead of rounding
// e.g., 1257 → "1,2 k" (not "1,3 k")
const formatNumberTruncated = (num) => {
  if (typeof num !== 'number' || isNaN(num)) return '0';
  if (num >= 1000000) {
    const truncated = Math.floor(num / 100000) / 10;
    return truncated.toFixed(1).replace('.', ',') + ' M';
  }
  if (num >= 1000) {
    const truncated = Math.floor(num / 100) / 10;
    return truncated.toFixed(1).replace('.', ',') + ' k';
  }
  return num.toString();
};

// Keep the original formatter for backward compatibility but prefer formatNumberTruncated
const numberFormatter = {
  format: formatNumberTruncated
};

const getNestedValue = (obj, path) => {
  if (!obj || !path) return undefined;
  return path.split('.').reduce((acc, key) => {
    if (acc && Object.prototype.hasOwnProperty.call(acc, key)) {
      return acc[key];
    }
    return undefined;
  }, obj);
};

const formatPercentageValue = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return `${value.toFixed(2)}%`;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (trimmed.endsWith('%')) {
      return trimmed;
    }
    const numeric = parseFloat(trimmed);
    if (!Number.isNaN(numeric)) {
      return `${numeric.toFixed(2)}%`;
    }
  }
  return null;
};

const getCampaignRateValue = (campaign, type) => {
  if (!campaign) return '—';
  const pathSets = {
    open: [
      'stats_summary.open_rate',
      'statsSummary.open_rate',
      'statistics.open_rate',
      'analysis.stats_summary.open_rate',
      'analysis.statsSummary.open_rate',
      'metrics.open_rate',
      'metrics.openRate',
      'open_rate',
      'openRate'
    ],
    click: [
      'stats_summary.click_rate',
      'statsSummary.click_rate',
      'statistics.click_rate',
      'analysis.stats_summary.click_rate',
      'analysis.statsSummary.click_rate',
      'metrics.click_rate',
      'metrics.clickRate',
      'click_rate',
      'clickRate'
    ]
  };

  const paths = pathSets[type] || [];
  for (const path of paths) {
    const raw = getNestedValue(campaign, path);
    const formatted = formatPercentageValue(raw);
    if (formatted) {
      return formatted;
    }
  }
  return '—';
};

const getStartOfDay = (value) => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

const getEndOfDay = (value) => {
  const date = new Date(value);
  date.setHours(23, 59, 59, 999);
  return date;
};

const Dashboard = () => {
  const { currentBrand } = useBrand();

  // Shared AbortController ref — aborted on unmount so ALL in-flight requests
  // are cancelled simultaneously when the user navigates away.
  const abortControllerRef = useRef(null);

  const getAbortSignal = useCallback(() => {
    // Reuse existing controller if still alive, otherwise create a fresh one
    if (!abortControllerRef.current || abortControllerRef.current.signal.aborted) {
      abortControllerRef.current = new AbortController();
    }
    return abortControllerRef.current.signal;
  }, []);

  // Master cleanup: abort all in-flight requests when the component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);
  const [timescale, setTimescale] = useState('day');
  const [anchorEl, setAnchorEl] = useState(null);

  // Initialize with last 30 days
  const getDefaultDateRange = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 29);
    return {
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0]
    };
  };

  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [tempDateRange, setTempDateRange] = useState(getDefaultDateRange());
  const [dateError, setDateError] = useState('');
  const [profileActivityDates, setProfileActivityDates] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState('');
  const [reloadCounter, setReloadCounter] = useState(0);
  const [favoriteSegments, setFavoriteSegments] = useState([]);
  const [favoriteSegmentCounts, setFavoriteSegmentCounts] = useState({});
  const [favoriteSegmentsLoading, setFavoriteSegmentsLoading] = useState(false);
  const [favoriteSegmentsError, setFavoriteSegmentsError] = useState('');
  const [showAllFavorites, setShowAllFavorites] = useState(false);
  const [favoriteReloadCounter, setFavoriteReloadCounter] = useState(0);
  const [recentCampaigns, setRecentCampaigns] = useState([]);
  const [recentCampaignsLoading, setRecentCampaignsLoading] = useState(false);
  const [recentCampaignsError, setRecentCampaignsError] = useState('');
  const [campaignReloadCounter, setCampaignReloadCounter] = useState(0);
  const [originStats, setOriginStats] = useState({ amplify: 0, social_media: 0, website: 0, total: 0 });
  const [originStatsLoading, setOriginStatsLoading] = useState(false);
  const [originStatsError, setOriginStatsError] = useState('');
  const [originStatsReloadCounter, setOriginStatsReloadCounter] = useState(0);
  const [activeContacts, setActiveContacts] = useState(0);
  const [activeContactsLoading, setActiveContactsLoading] = useState(false);
  const [activeContactsError, setActiveContactsError] = useState('');
  const [activeContactsReloadCounter, setActiveContactsReloadCounter] = useState(0);
  const [emailOpenRate, setEmailOpenRate] = useState({ open_rate: 0, sent_count: 0, open_count: 0 });
  const [emailOpenRateLoading, setEmailOpenRateLoading] = useState(false);
  const [emailOpenRateError, setEmailOpenRateError] = useState('');
  const [emailOpenRateReloadCounter, setEmailOpenRateReloadCounter] = useState(0);
  const [clickRate, setClickRate] = useState({ click_rate: 0, sent_count: 0, click_count: 0 });
  const [clickRateLoading, setClickRateLoading] = useState(false);
  const [clickRateError, setClickRateError] = useState('');
  const [clickRateReloadCounter, setClickRateReloadCounter] = useState(0);

  // Loyalty KPIs state
  const [loyaltyKpis, setLoyaltyKpis] = useState(null);
  const [loyaltyKpisLoading, setLoyaltyKpisLoading] = useState(false);
  const [loyaltyKpisError, setLoyaltyKpisError] = useState('');
  const [loyaltyKpisReloadCounter, setLoyaltyKpisReloadCounter] = useState(0);
  const [loyaltyCustomersOpen, setLoyaltyCustomersOpen] = useState(false);
  const [loyaltyCustomersLoading, setLoyaltyCustomersLoading] = useState(false);
  const [loyaltyCustomersError, setLoyaltyCustomersError] = useState('');
  const [loyaltyCustomers, setLoyaltyCustomers] = useState([]);
  const [loyaltyCustomersCount, setLoyaltyCustomersCount] = useState(0);
  const [loyaltyCustomersPage, setLoyaltyCustomersPage] = useState(0);

  const loyaltyCustomersLimit = 20;

  const favoriteVisibleLimit = 5;

  const handleCalendarClick = (event) => {
    setAnchorEl(event.currentTarget);
    setTempDateRange(dateRange); // Set temp to current when opening
    setDateError('');
  };

  const handleCalendarClose = () => {
    setAnchorEl(null);
    setDateError('');
  };

  const handleDateChange = (field, value) => {
    const newTempRange = { ...tempDateRange, [field]: value };
    setTempDateRange(newTempRange);

    // Validate dates
    if (newTempRange.start && newTempRange.end) {
      const start = new Date(newTempRange.start);
      const end = new Date(newTempRange.end);
      if (end < start) {
        setDateError('La date de fin doit être postérieure à la date de début');
      } else {
        setDateError('');
      }
    }
  };

  const handleApplyDateRange = () => {
    if (tempDateRange.start && tempDateRange.end) {
      const start = new Date(tempDateRange.start);
      const end = new Date(tempDateRange.end);
      if (end < start) {
        setDateError('La date de fin doit être postérieure à la date de début');
        return;
      }
    }
    setDateRange(tempDateRange);
    setAnchorEl(null);
    setDateError('');
  };

  const formatDateRange = () => {
    const start = new Date(dateRange.start);
    const end = new Date(dateRange.end);
    const options = { day: '2-digit', month: 'long', year: 'numeric' };
    return `${start.toLocaleDateString('fr-FR', options)} - ${end.toLocaleDateString('fr-FR', options)}`;
  };

  const open = Boolean(anchorEl);

  // Helper function to get week number
  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  useEffect(() => {
    let isMounted = true;
    // Refresh the shared controller when this effect fires (e.g. brand/date change)
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;
    const startDate = getStartOfDay(dateRange.start);
    const endDate = getEndOfDay(dateRange.end);

    const fetchProfiles = async () => {
      if (!currentBrand) {
        setProfileActivityDates([]);
        return;
      }

      setChartLoading(true);
      setChartError('');

      try {
        const limit = 200;
        let offset = 0;
        let allProfiles = [];
        let hasMore = true;
        let iterations = 0;
        const maxIterations = 10;

        while (hasMore && iterations < maxIterations) {
          if (!isMounted || signal.aborted) return;

          const response = await api.post('/profiles/search', {
            offset,
            limit,
            search_term: null,
            segment_id: null,
            sort_by: 'properties.firstVisit:desc'
          }, {
            signal
          });

          if (!isMounted || signal.aborted) return;

          const data = response.data || {};
          const profiles = data.profiles || [];
          const total = data.total || 0;

          allProfiles = allProfiles.concat(profiles);
          offset += limit;
          iterations += 1;

          const fetchedAll = offset >= total || profiles.length < limit;
          hasMore = !fetchedAll;

          if (hasMore && profiles.length) {
            const lastProfile = profiles[profiles.length - 1];
            const lastDateString =
              lastProfile?.properties?.firstVisit ||
              lastProfile?.properties?.lastVisit ||
              lastProfile?.systemProperties?.lastUpdated;

            if (lastDateString) {
              const lastDate = new Date(lastDateString);
              if (lastDate < startDate) {
                hasMore = false;
              }
            }
          }
        }

        const filteredDates = allProfiles
          .map((profile) => {
            const dateString =
              profile?.properties?.firstVisit ||
              profile?.properties?.lastVisit ||
              profile?.systemProperties?.lastUpdated;

            if (!dateString) return null;
            const parsed = new Date(dateString);
            if (Number.isNaN(parsed.getTime())) return null;
            return parsed;
          })
          .filter(
            (dateValue) =>
              dateValue &&
              dateValue >= startDate &&
              dateValue <= endDate
          );

        if (!isMounted || signal.aborted) return;

        setProfileActivityDates(filteredDates);
      } catch (error) {
        if (axios.isCancel(error) || error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') {
          console.log('Profile fetch canceled — user navigated away');
          return;
        }
        if (!isMounted) return;
        console.error('Error loading dashboard profiles:', error);
        setChartError(error.response?.data?.detail || 'Erreur lors du chargement des données');
        setProfileActivityDates([]);
      } finally {
        if (isMounted && !signal.aborted) {
          setChartLoading(false);
        }
      }
    };

    fetchProfiles();

    return () => {
      isMounted = false;
      // Do NOT abort shared controller here — the master cleanup useEffect handles that.
      // Only mark as unmounted so state setters are skipped.
    };
  }, [currentBrand?.id, dateRange.start, dateRange.end, timescale, reloadCounter]);

  useEffect(() => {
    let isMounted = true;
    const signal = getAbortSignal();

    const loadFavoriteSegments = async () => {
      if (!currentBrand?.id) {
        setFavoriteSegments([]);
        setFavoriteSegmentCounts({});
        return;
      }

      setFavoriteSegmentsLoading(true);
      setFavoriteSegmentsError('');

      try {
        const response = await segmentService.getSegmentsPaginated({
          page: 1,
          pageSize: 20,
          sortField: 'created',
          sortDirection: 'desc',
          favoritesOnly: true,
          signal
        });

        if (!isMounted || signal.aborted) return;

        const segments = response?.segments || [];
        setFavoriteSegments(segments);
        setShowAllFavorites(false);

        const segmentIds = segments.map((segment) => segment.id).filter(Boolean);
        if (segmentIds.length) {
          const counts = await segmentService.getBatchProfileCounts(segmentIds, { signal });
          if (isMounted && !signal.aborted) {
            setFavoriteSegmentCounts(counts || {});
          }
        } else {
          if (isMounted) setFavoriteSegmentCounts({});
        }
      } catch (error) {
        if (axios.isCancel(error) || error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') return;
        if (!isMounted) return;
        console.error('Error loading favorite segments:', error);
        setFavoriteSegmentsError(error.response?.data?.detail || 'Impossible de charger vos segments favoris');
        setFavoriteSegments([]);
        setFavoriteSegmentCounts({});
      } finally {
        if (isMounted && !signal.aborted) {
          setFavoriteSegmentsLoading(false);
        }
      }
    };

    loadFavoriteSegments();

    return () => { isMounted = false; };
  }, [currentBrand?.id, favoriteReloadCounter, getAbortSignal]);

  useEffect(() => {
    let isMounted = true;
    const signal = getAbortSignal();

    const fetchRecentCampaigns = async () => {
      if (!currentBrand?.id) {
        setRecentCampaigns([]);
        return;
      }

      setRecentCampaignsLoading(true);
      setRecentCampaignsError('');

      try {
        const response = await api.get('/campaigns', {
          params: {
            page: 1,
            page_size: 5,
            sort_field: 'created',
            sort_direction: 'desc',
            with_stats: true
          },
          signal
        });

        if (!isMounted || signal.aborted) return;

        const data = response?.data;
        if (data?.success === false) {
          throw new Error(data?.message || 'Erreur lors du chargement des campagnes');
        }

        const campaigns = Array.isArray(data?.campaigns) ? data.campaigns : [];
        setRecentCampaigns(campaigns);
      } catch (error) {
        if (axios.isCancel(error) || error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') return;
        if (!isMounted) return;
        console.error('Error loading recent campaigns:', error);
        setRecentCampaignsError(error.response?.data?.detail || error.message || 'Erreur lors du chargement des campagnes');
        setRecentCampaigns([]);
      } finally {
        if (isMounted && !signal.aborted) {
          setRecentCampaignsLoading(false);
        }
      }
    };

    fetchRecentCampaigns();

    return () => { isMounted = false; };
  }, [currentBrand?.id, campaignReloadCounter, getAbortSignal]);

  useEffect(() => {
    let isMounted = true;
    const signal = getAbortSignal();

    const fetchOriginStats = async () => {
      if (!currentBrand?.id) {
        setOriginStats({ amplify: 0, social_media: 0, website: 0, total: 0 });
        return;
      }

      setOriginStatsLoading(true);
      setOriginStatsError('');

      try {
        const response = await api.get('/profiles/origin-stats', { signal });

        if (!isMounted || signal.aborted) return;

        const data = response?.data || {};
        setOriginStats({
          amplify: data.amplify || 0,
          social_media: data.social_media || 0,
          website: data.website || 0,
          total: data.total || 0
        });
      } catch (error) {
        if (axios.isCancel(error) || error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') return;
        if (!isMounted) return;
        console.error('Error loading origin stats:', error);
        setOriginStatsError(error.response?.data?.detail || error.message || 'Erreur lors du chargement des statistiques d\'origine');
        setOriginStats({ amplify: 0, social_media: 0, website: 0, total: 0 });
      } finally {
        if (isMounted && !signal.aborted) {
          setOriginStatsLoading(false);
        }
      }
    };

    fetchOriginStats();

    return () => { isMounted = false; };
  }, [currentBrand?.id, originStatsReloadCounter, getAbortSignal]);

  // Fetch active contacts count (profiles that received email in last 12 months)
  useEffect(() => {
    let isMounted = true;
    const signal = getAbortSignal();

    const fetchActiveContacts = async () => {
      if (!currentBrand?.id) {
        setActiveContacts(0);
        return;
      }

      setActiveContactsLoading(true);
      setActiveContactsError('');

      try {
        const response = await api.get('/profiles/active-contacts', {
          params: { months: 12 },
          signal
        });

        if (!isMounted || signal.aborted) return;

        const data = response?.data || {};
        setActiveContacts(data.active_contacts || 0);
      } catch (error) {
        if (axios.isCancel(error) || error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') return;
        if (!isMounted) return;
        console.error('Error loading active contacts:', error);
        setActiveContactsError(error.response?.data?.detail || error.message || 'Erreur lors du chargement des contacts actifs');
        setActiveContacts(0);
      } finally {
        if (isMounted && !signal.aborted) {
          setActiveContactsLoading(false);
        }
      }
    };

    fetchActiveContacts();

    return () => { isMounted = false; };
  }, [currentBrand?.id, activeContactsReloadCounter, getAbortSignal]);

  // Fetch email open rate (emailopen / emailsent * 100)
  useEffect(() => {
    let isMounted = true;
    const signal = getAbortSignal();

    const fetchEmailOpenRate = async () => {
      if (!currentBrand?.id) {
        setEmailOpenRate({ open_rate: 0, sent_count: 0, open_count: 0 });
        return;
      }

      setEmailOpenRateLoading(true);
      setEmailOpenRateError('');

      try {
        const response = await api.get('/profiles/email-open-rate', {
          params: { months: 12 },
          signal
        });

        if (!isMounted || signal.aborted) return;

        const data = response?.data || {};
        setEmailOpenRate({
          open_rate: data.open_rate || 0,
          sent_count: data.sent_count || 0,
          open_count: data.open_count || 0
        });
      } catch (error) {
        if (axios.isCancel(error) || error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') return;
        if (!isMounted) return;
        console.error('Error loading email open rate:', error);
        setEmailOpenRateError(error.response?.data?.detail || error.message || 'Erreur lors du chargement du taux d\'ouverture');
        setEmailOpenRate({ open_rate: 0, sent_count: 0, open_count: 0 });
      } finally {
        if (isMounted && !signal.aborted) {
          setEmailOpenRateLoading(false);
        }
      }
    };

    fetchEmailOpenRate();

    return () => { isMounted = false; };
  }, [currentBrand?.id, emailOpenRateReloadCounter, getAbortSignal]);

  // Fetch click rate (page_on_hit from email / emailsent * 100)
  useEffect(() => {
    let isMounted = true;
    const signal = getAbortSignal();

    const fetchClickRate = async () => {
      if (!currentBrand?.id) {
        setClickRate({ click_rate: 0, sent_count: 0, click_count: 0 });
        return;
      }

      setClickRateLoading(true);
      setClickRateError('');

      try {
        const response = await api.get('/profiles/click-rate', {
          params: { months: 12 },
          signal
        });

        if (!isMounted || signal.aborted) return;

        const data = response?.data || {};
        setClickRate({
          click_rate: data.click_rate || 0,
          sent_count: data.sent_count || 0,
          click_count: data.click_count || 0
        });
      } catch (error) {
        if (axios.isCancel(error) || error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') return;
        if (!isMounted) return;
        console.error('Error loading click rate:', error);
        setClickRateError(error.response?.data?.detail || error.message || 'Erreur lors du chargement du taux de clics');
        setClickRate({ click_rate: 0, sent_count: 0, click_count: 0 });
      } finally {
        if (isMounted && !signal.aborted) {
          setClickRateLoading(false);
        }
      }
    };

    fetchClickRate();

    return () => { isMounted = false; };
  }, [currentBrand?.id, clickRateReloadCounter, getAbortSignal]);

  // Fetch Loyalty KPIs
  useEffect(() => {
    let isMounted = true;
    const signal = getAbortSignal();

    const fetchLoyaltyKpis = async () => {
      if (!currentBrand?.name) {
        setLoyaltyKpis(null);
        return;
      }

      setLoyaltyKpisLoading(true);
      setLoyaltyKpisError('');

      try {
        // We use currentBrand.name since the loyalty backend identifies brands by name
        const headers = { "X-Brand": currentBrand.name };
        const response = await loyaltyFetch(`${LOYALTY_API_BASE}/admin/brand-kpis?windowDays=30`, { headers, signal });
        
        if (!isMounted || signal.aborted) return;
        
        if (!response.ok) {
           throw new Error("Erreur de l'API fidélité");
        }
        
        const data = await response.json();
        setLoyaltyKpis(data);
      } catch (error) {
        if (error.name === 'AbortError' || error.message?.includes('aborted')) return;
        if (!isMounted) return;
        console.error('Error loading loyalty kpis:', error);
        setLoyaltyKpisError(error.message || 'Erreur lors du chargement des KPIs');
        setLoyaltyKpis(null);
      } finally {
        if (isMounted && !signal.aborted) {
          setLoyaltyKpisLoading(false);
        }
      }
    };

    fetchLoyaltyKpis();

    return () => { isMounted = false; };
  }, [currentBrand?.name, loyaltyKpisReloadCounter, getAbortSignal]);

  const handleRetryLoad = () => setReloadCounter((prev) => prev + 1);
  const handleReloadFavoriteSegments = () => setFavoriteReloadCounter((prev) => prev + 1);
  const handleReloadCampaigns = () => setCampaignReloadCounter((prev) => prev + 1);
  const handleReloadOriginStats = () => setOriginStatsReloadCounter((prev) => prev + 1);
  const handleReloadActiveContacts = () => setActiveContactsReloadCounter((prev) => prev + 1);
  const handleReloadEmailOpenRate = () => setEmailOpenRateReloadCounter((prev) => prev + 1);
  const handleReloadClickRate = () => setClickRateReloadCounter((prev) => prev + 1);
  const handleReloadLoyaltyKpis = () => setLoyaltyKpisReloadCounter((prev) => prev + 1);
  const loadLoyaltyCustomers = useCallback(async (page = 0) => {
    if (!currentBrand?.name) return;
    setLoyaltyCustomersLoading(true);
    setLoyaltyCustomersError('');
    try {
      const headers = { "X-Brand": currentBrand.name };
      const offset = page * loyaltyCustomersLimit;
      const response = await loyaltyFetch(
        `${LOYALTY_API_BASE}/customers?limit=${loyaltyCustomersLimit}&offset=${offset}`,
        { headers }
      );
      if (!response.ok) {
        throw new Error("Erreur lors du chargement des clients fidélité");
      }
      const data = await response.json();
      setLoyaltyCustomers(Array.isArray(data?.items) ? data.items : []);
      setLoyaltyCustomersCount(Number(data?.count || 0));
      setLoyaltyCustomersPage(page);
    } catch (error) {
      console.error('Error loading loyalty customers:', error);
      setLoyaltyCustomersError(error.message || 'Erreur lors du chargement des clients fidélité');
      setLoyaltyCustomers([]);
      setLoyaltyCustomersCount(0);
    } finally {
      setLoyaltyCustomersLoading(false);
    }
  }, [currentBrand?.name]);

  const handleOpenLoyaltyCustomers = async () => {
    setLoyaltyCustomersOpen(true);
    await loadLoyaltyCustomers(0);
  };

  const handleCloseLoyaltyCustomers = () => {
    setLoyaltyCustomersOpen(false);
  };

  const handleLoyaltyCustomersPrev = async () => {
    const prevPage = Math.max(0, loyaltyCustomersPage - 1);
    if (prevPage !== loyaltyCustomersPage) {
      await loadLoyaltyCustomers(prevPage);
    }
  };

  const handleLoyaltyCustomersNext = async () => {
    const nextPage = loyaltyCustomersPage + 1;
    const maxOffset = nextPage * loyaltyCustomersLimit;
    if (maxOffset < loyaltyCustomersCount) {
      await loadLoyaltyCustomers(nextPage);
    }
  };
  const getCampaignDisplayName = (campaign) => {
    if (!campaign) return 'Campagne';
    return (
      campaign.name ||
      campaign.campaign_name ||
      campaign.title ||
      `Campagne ${campaign.id ?? ''}`
    );
  };

  const displayedFavoriteSegments = useMemo(() => {
    if (showAllFavorites) {
      return favoriteSegments;
    }
    return favoriteSegments.slice(0, favoriteVisibleLimit);
  }, [favoriteSegments, showAllFavorites]);

  const hasMoreFavoriteSegments = favoriteSegments.length > favoriteVisibleLimit;
  const shouldShowSeeMore = hasMoreFavoriteSegments && !showAllFavorites;
  const shouldShowSeeLess = hasMoreFavoriteSegments && showAllFavorites;

  const getFavoriteSegmentCount = (segment) => {
    if (!segment) return null;
    const mapped = favoriteSegmentCounts?.[segment.id];
    if (typeof mapped === 'number') {
      return mapped;
    }
    if (typeof segment.profileCount === 'number') {
      return segment.profileCount;
    }
    if (typeof segment.profile_count === 'number') {
      return segment.profile_count;
    }
    if (typeof segment.profiles === 'number') {
      return segment.profiles;
    }
    return null;
  };

  const formatContactCount = (count) => {
    if (typeof count !== 'number') {
      return '—';
    }
    return numberFormatter.format(count);
  };

  const chartData = useMemo(() => {
    const start = getStartOfDay(dateRange.start);
    const end = getEndOfDay(dateRange.end);
    const diffTime = Math.abs(end - start);
    const msPerDay = 1000 * 60 * 60 * 24;
    const totalDays = Math.max(1, Math.ceil(diffTime / msPerDay) + 1);

    const dayBuckets = new Map();
    profileActivityDates.forEach((dateValue) => {
      if (!dateValue) return;
      const normalized = new Date(Date.UTC(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate()));
      const key = normalized.toISOString().split('T')[0];
      dayBuckets.set(key, (dayBuckets.get(key) || 0) + 1);
    });

    const formatDayLabel = (dateValue) => {
      const day = dateValue.getDate().toString().padStart(2, '0');
      const month = (dateValue.getMonth() + 1).toString().padStart(2, '0');
      return `${day}/${month}`;
    };

    const getDayCount = (dateValue) => {
      const normalized = new Date(Date.UTC(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate()));
      const key = normalized.toISOString().split('T')[0];
      return dayBuckets.get(key) || 0;
    };

    let labels = [];
    let data = [];

    if (timescale === 'day') {
      const maxDays = 31;
      const daysToShow = Math.min(totalDays, maxDays);
      const startDay = new Date(totalDays > maxDays ? end : start);

      if (totalDays > maxDays) {
        startDay.setDate(startDay.getDate() - maxDays + 1);
      }

      for (let i = 0; i < daysToShow; i++) {
        const dayDate = new Date(startDay);
        dayDate.setDate(startDay.getDate() + i);
        if (dayDate < start || dayDate > end) continue;
        labels.push(formatDayLabel(dayDate));
        data.push(getDayCount(dayDate));
      }
    } else if (timescale === 'week') {
      // Show weeks within the selected range (up to 12 weeks)
      const maxWeeks = 12;
      const totalWeeks = Math.max(1, Math.ceil(totalDays / 7));
      const weeksToShow = Math.min(totalWeeks, maxWeeks);

      // Start from the beginning of the range (or maxWeeks before end)
      let startWeek = new Date(totalWeeks > maxWeeks ? end : start);
      if (totalWeeks > maxWeeks) {
        // If range is too large, show last maxWeeks
        startWeek.setDate(startWeek.getDate() - (maxWeeks * 7) + 1);
      }

      let currentDate = new Date(startWeek);
      for (let i = 0; i < weeksToShow; i++) {
        if (currentDate > end) break;
        const weekNum = getWeekNumber(currentDate);
        labels.push(`S${weekNum}`);

        let weekSum = 0;
        for (let d = 0; d < 7; d++) {
          const dayDate = new Date(currentDate);
          dayDate.setDate(currentDate.getDate() + d);
          if (dayDate < start || dayDate > end) continue;
          weekSum += getDayCount(dayDate);
        }
        data.push(Math.round(weekSum));

        currentDate.setDate(currentDate.getDate() + 7);
      }
    } else if (timescale === 'month') {
      const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
      const maxMonths = 12;

      const totalMonths = Math.max(
        1,
        (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1
      );
      const monthsToShow = Math.min(totalMonths, maxMonths);

      const startMonth = new Date(
        totalMonths > maxMonths ? end.getFullYear() : start.getFullYear(),
        totalMonths > maxMonths ? end.getMonth() : start.getMonth(),
        1
      );
      if (totalMonths > maxMonths) {
        startMonth.setMonth(startMonth.getMonth() - (maxMonths - 1));
      }

      let monthCounter = 0;
      let currentDate = new Date(startMonth);
      while (monthCounter < monthsToShow && currentDate <= end) {
        labels.push(monthNames[currentDate.getMonth()]);

        let monthSum = 0;
        const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();

        for (let d = 1; d <= daysInMonth; d++) {
          const dayDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), d);
          if (dayDate < start || dayDate > end) continue;
          monthSum += getDayCount(dayDate);
        }

        data.push(Math.round(monthSum));

        currentDate.setMonth(currentDate.getMonth() + 1);
        monthCounter++;
      }
    }

    const result = {
      labels,
      datasets: [{
        label: 'Volume de contacts',
        data,
        backgroundColor: 'rgba(54, 162, 235, 0.7)',
        borderRadius: 4,
        barPercentage: 0.6,
      }]
    };

    return result;
  }, [dateRange.start, dateRange.end, timescale, profileActivityDates]);

  const hasChartData = chartData.datasets?.[0]?.data?.some((value) => value > 0);
  const chartKey = `${timescale}-${dateRange.start}-${dateRange.end}-${currentBrand?.id || 'default'}`;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          usePointStyle: true,
          boxWidth: 6,
          padding: 20,
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        border: {
          display: false
        }
      },
      y: {
        grid: {
          color: '#f9fafb'
        },
        border: {
          display: false
        }
      }
    }
  };

  const originData = useMemo(() => {
    const amplify = originStats.amplify || 0;
    const socialMedia = originStats.social_media || 0;
    const website = originStats.website || 0;

    return {
      labels: ['Amplify', 'Social media', 'Website'],
      datasets: [{
        data: [amplify, socialMedia, website],
        backgroundColor: ['#6366F1', '#2DD4BF', '#D1D5DB'],
        borderWidth: 4,
        borderColor: '#fff',
      }]
    };
  }, [originStats]);

  const originOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '70%',
    plugins: {
      legend: {
        display: false
      }
    }
  };

  return (
    <Box m={{ xs: 1, md: 2 }}>
      {/* Header with Date Range Picker */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Header
            title="Dashboard"
            subtitle="Aperçu de l'activité de vos contacts et de la performance de vos campagnes."
            hideVersion={true}
          />
        </Box>

        {/* Date Range Picker Button */}
        <Button
          variant="outlined"
          startIcon={<CalendarTodayIcon />}
          onClick={handleCalendarClick}
          sx={{
            textTransform: 'none',
            borderColor: '#D1D5DB',
            color: '#1F2937',
            bgcolor: '#fff',
            px: 2,
            py: 1,
            minWidth: '280px',
            justifyContent: 'flex-start',
            '&:hover': {
              borderColor: '#9CA3AF',
              bgcolor: '#F9FAFB'
            }
          }}
        >
          {formatDateRange()}
        </Button>

        {/* Date Range Popover */}
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleCalendarClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Box sx={{ p: 3, minWidth: 320 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#1F2937' }}>
              Sélectionner la période
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Date de début"
                type="date"
                value={tempDateRange.start}
                onChange={(e) => handleDateChange('start', e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
                error={!!dateError}
              />
              <TextField
                label="Date de fin"
                type="date"
                value={tempDateRange.end}
                onChange={(e) => handleDateChange('end', e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
                error={!!dateError}
                helperText={dateError}
              />
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
                <Button
                  size="small"
                  onClick={handleCalendarClose}
                  sx={{ textTransform: 'none' }}
                >
                  Annuler
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleApplyDateRange}
                  disabled={!!dateError}
                  sx={{
                    textTransform: 'none',
                    bgcolor: '#14B8A6',
                    '&:hover': { bgcolor: '#0F9488' },
                    '&:disabled': { bgcolor: '#D1D5DB' }
                  }}
                >
                  Appliquer
                </Button>
              </Box>
            </Box>
          </Box>
        </Popover>
      </Box>

      <Grid container spacing={3}>
        {/* LEFT SECTION - 2/3 width */}
        <Grid item xs={12} lg={8}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Main Chart Card */}
            <Card sx={{ p: 3, borderRadius: 2, border: '1px solid #e5e7eb', boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)' }}>
              {/* Header with filters */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 2, mb: 2, borderBottom: '1px solid #e5e7eb' }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1F2937' }}>
                    Activité des Contacts
                  </Typography>
                  {currentBrand?.name && (
                    <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
                      Marque active : {currentBrand.name}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  {/* Timescale filter */}
                  <ButtonGroup size="small" sx={{ '& .MuiButton-root': { textTransform: 'none', fontSize: '0.75rem', px: 2 } }}>
                    <Button
                      variant={timescale === 'day' ? 'contained' : 'text'}
                      onClick={() => setTimescale('day')}
                      sx={{
                        bgcolor: timescale === 'day' ? '#1F2937' : 'transparent',
                        color: timescale === 'day' ? '#fff' : '#6B7280',
                        '&:hover': { bgcolor: timescale === 'day' ? '#374151' : '#F3F4F6' }
                      }}
                    >
                      Jour
                    </Button>
                    <Button
                      variant={timescale === 'week' ? 'contained' : 'text'}
                      onClick={() => setTimescale('week')}
                      sx={{
                        bgcolor: timescale === 'week' ? '#1F2937' : 'transparent',
                        color: timescale === 'week' ? '#fff' : '#6B7280',
                        '&:hover': { bgcolor: timescale === 'week' ? '#374151' : '#F3F4F6' }
                      }}
                    >
                      Semaine
                    </Button>
                    <Button
                      variant={timescale === 'month' ? 'contained' : 'text'}
                      onClick={() => setTimescale('month')}
                      sx={{
                        bgcolor: timescale === 'month' ? '#1F2937' : 'transparent',
                        color: timescale === 'month' ? '#fff' : '#6B7280',
                        '&:hover': { bgcolor: timescale === 'month' ? '#374151' : '#F3F4F6' }
                      }}
                    >
                      Mois
                    </Button>
                  </ButtonGroup>
                </Box>
              </Box>

              {/* Chart */}
              <Box sx={{ height: 280, mb: 3, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                {chartLoading ? (
                  <CircularProgress />
                ) : chartError ? (
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                      {chartError}
                    </Typography>
                    <Button variant="outlined" size="small" onClick={handleRetryLoad}>
                      Réessayer
                    </Button>
                  </Box>
                ) : !hasChartData ? (
                  <Typography variant="body2" color="text.secondary">
                    Aucune donnée disponible pour cette période.
                  </Typography>
                ) : (
                  <Bar data={chartData} options={chartOptions} key={chartKey} />
                )}
              </Box>

              {/* Stats Grid */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, pt: 2, borderTop: '1px solid #e5e7eb' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mb: 0.5 }}>
                    Contacts
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#1F2937' }}>
                    {originStatsLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      numberFormatter.format(originStats.total || 0)
                    )}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mb: 0.5 }}>
                    Contacts Actifs
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#1F2937' }}>
                    {activeContactsLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      numberFormatter.format(activeContacts || 0)
                    )}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mb: 0.5 }}>
                    Taux d'Ouverture
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#1F2937' }}>
                    {emailOpenRateLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      `${emailOpenRate.open_rate || 0}%`
                    )}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mb: 0.5 }}>
                    Taux de Clics
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#1F2937' }}>
                    {clickRateLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      `${clickRate.click_rate || 0}%`
                    )}
                  </Typography>
                </Box>
              </Box>
            </Card>

            {/* Bottom Row - Segments and Campaigns */}
            <Grid container spacing={3}>
              {/* Segments Card */}
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 3, borderRadius: 2, border: '1px solid #e5e7eb', boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)', height: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 2, mb: 2, borderBottom: '1px solid #e5e7eb' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', color: '#1F2937' }}>
                      Segments favoris
                    </Typography>
                    <Button
                      component={RouterLink}
                      to="/segment"
                      size="small"
                      sx={{
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: '#14B8A6',
                        '&:hover': { textDecoration: 'underline', bgcolor: 'transparent' }
                      }}
                    >
                      Voir tout →
                    </Button>
                  </Box>
                  {favoriteSegmentsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : favoriteSegmentsError ? (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                        {favoriteSegmentsError}
                      </Typography>
                      <Button size="small" variant="outlined" onClick={handleReloadFavoriteSegments}>
                        Réessayer
                      </Button>
                    </Box>
                  ) : favoriteSegments.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <Typography variant="body2" sx={{ color: '#6B7280', mb: 1 }}>
                        Aucun segment favori pour cette marque pour le moment.
                      </Typography>
                      <Button
                        component={RouterLink}
                        to="/segment"
                        size="small"
                        sx={{ textTransform: 'none', fontWeight: 600, color: '#14B8A6' }}
                      >
                        Gérer les segments →
                      </Button>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {displayedFavoriteSegments.map((segment) => (
                        <Box
                          key={segment.id}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            border: '1px solid #e5e7eb',
                            borderRadius: 1.5,
                            p: 1.5,
                            gap: 1.5,
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1F2937', mb: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {segment.name || segment.metadata?.name || 'Segment sans nom'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6B7280', display: 'block' }}>
                              {segment.metadata?.description || 'Segment favori'}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1F2937' }}>
                              {formatContactCount(getFavoriteSegmentCount(segment))} contacts
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                              Mis à jour récemment
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                      {shouldShowSeeMore && (
                        <Button
                          onClick={() => setShowAllFavorites(true)}
                          size="small"
                          sx={{ textTransform: 'none', fontWeight: 600, color: '#14B8A6', alignSelf: 'flex-start' }}
                        >
                          Voir plus
                        </Button>
                      )}
                      {shouldShowSeeLess && (
                        <Button
                          onClick={() => setShowAllFavorites(false)}
                          size="small"
                          sx={{ textTransform: 'none', fontWeight: 600, color: '#14B8A6', alignSelf: 'flex-start' }}
                        >
                          Voir moins
                        </Button>
                      )}
                    </Box>
                  )}
                </Card>
              </Grid>

              {/* Campaigns Card */}
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 3, borderRadius: 2, border: '1px solid #e5e7eb', boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)', height: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 2, mb: 2, borderBottom: '1px solid #e5e7eb' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', color: '#1F2937' }}>
                      Campagnes Récentes
                    </Typography>
                    <Button
                      component={RouterLink}
                      to="/campagne"
                      size="small"
                      sx={{
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: '#14B8A6',
                        '&:hover': { textDecoration: 'underline', bgcolor: 'transparent' }
                      }}
                    >
                      Toutes les campagnes →
                    </Button>
                  </Box>
                  {recentCampaignsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : recentCampaignsError ? (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                        {recentCampaignsError}
                      </Typography>
                      <Button size="small" variant="outlined" onClick={handleReloadCampaigns}>
                        Réessayer
                      </Button>
                    </Box>
                  ) : recentCampaigns.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <Typography variant="body2" sx={{ color: '#6B7280', mb: 1 }}>
                        Aucune campagne récente pour cette marque.
                      </Typography>
                      <Button
                        component={RouterLink}
                        to="/campagne"
                        size="small"
                        sx={{ textTransform: 'none', fontWeight: 600, color: '#14B8A6' }}
                      >
                        Créer une campagne →
                      </Button>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                      {recentCampaigns.map((campaign, index) => {
                        const openRate = getCampaignRateValue(campaign, 'open');
                        const clickRate = getCampaignRateValue(campaign, 'click');
                        return (
                          <Box
                            key={campaign?.id ?? index}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              borderBottom: index < recentCampaigns.length - 1 ? '1px solid #f3f4f6' : 'none',
                              pb: 1.25,
                              mb: index < recentCampaigns.length - 1 ? 0.25 : 0
                            }}
                          >
                            <Typography variant="body2" sx={{ color: '#1F2937', fontWeight: 500, pr: 1, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {getCampaignDisplayName(campaign)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#1F2937', display: 'flex', alignItems: 'center', gap: 0.25 }}>
                              <Typography component="span" sx={{ fontWeight: 600 }}>
                                {openRate}
                              </Typography>
                              <Typography component="span" variant="caption" sx={{ color: '#9CA3AF' }}>
                                Ouv.
                              </Typography>
                              <Typography component="span" sx={{ color: '#9CA3AF', mx: 0.5 }}>
                                /
                              </Typography>
                              <Typography component="span" sx={{ fontWeight: 600 }}>
                                {clickRate}
                              </Typography>
                              <Typography component="span" variant="caption" sx={{ color: '#9CA3AF' }}>
                                Clics
                              </Typography>
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* RIGHT SIDEBAR - 1/3 width */}
        <Grid item xs={12} lg={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Origin Chart Card */}
            <Card sx={{ p: 3, borderRadius: 2, border: '1px solid #e5e7eb', boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)' }}>
              <Box sx={{ pb: 2, mb: 2, borderBottom: '1px solid #e5e7eb' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', color: '#1F2937' }}>
                  Origine des Contacts
                </Typography>
              </Box>
              <Box sx={{ height: 200, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                {originStatsLoading ? (
                  <CircularProgress size={40} />
                ) : originStatsError ? (
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="error" sx={{ mb: 1, fontSize: '0.75rem' }}>
                      {originStatsError}
                    </Typography>
                    <Button variant="outlined" size="small" onClick={handleReloadOriginStats} sx={{ textTransform: 'none' }}>
                      Réessayer
                    </Button>
                  </Box>
                ) : originStats.total === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Aucune donnée disponible
                  </Typography>
                ) : (
                  <>
                    <Doughnut data={originData} options={originOptions} />
                    <Typography
                      variant="h4"
                      sx={{
                        position: 'absolute',
                        fontWeight: 700,
                        color: '#1F2937',
                        pointerEvents: 'none'
                      }}
                    >
                      {numberFormatter.format(originStats.total)}
                    </Typography>
                  </>
                )}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#6366F1' }} />
                  <Typography variant="caption" sx={{ color: '#1F2937' }}>Qilinsa Amplify</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#2DD4BF' }} />
                  <Typography variant="caption" sx={{ color: '#1F2937' }}>Social media</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#D1D5DB' }} />
                  <Typography variant="caption" sx={{ color: '#1F2937' }}>Website</Typography>
                </Box>
              </Box>
            </Card>

            {/* AI Insights Card */}
            <Card sx={{ p: 3, borderRadius: 2, border: '1px solid #e5e7eb', boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)' }}>
              <Box sx={{ pb: 2, mb: 2, borderBottom: '1px solid #e5e7eb' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', color: '#1F2937', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box component="span" sx={{ fontSize: '1.25rem' }}>🤖</Box>
                  Insights de l'IA
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: '#FEF2F2', border: '1px solid #FEE2E2' }}>
                  <Typography variant="caption" sx={{ color: '#991B1B', fontSize: '0.75rem' }}>
                    <strong>Alerte :</strong> Chute de 15% du taux d'ouverture du segment "Clients VIP".
                  </Typography>
                </Box>
                <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: '#F0FDF4', border: '1px solid #BBF7D0' }}>
                  <Typography variant="caption" sx={{ color: '#14532D', fontSize: '0.75rem' }}>
                    <strong>Opportunité :</strong> Les contacts ayant acheté le Produit X montrent un fort intérêt pour la catégorie Y.
                  </Typography>
                </Box>
              </Box>
            </Card>

            {/* Loyalty KPIs Card */}
            <Card sx={{ p: 3, borderRadius: 2, border: '1px solid #e5e7eb', boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)' }}>
              <Box sx={{ pb: 2, mb: 2, borderBottom: '1px solid #e5e7eb' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', color: '#1F2937', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box component="span" sx={{ fontSize: '1.25rem' }}>🎁</Box>
                  Performance Fidélité (30j)
                </Typography>
              </Box>
              
              {loyaltyKpisLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={30} />
                </Box>
              ) : loyaltyKpisError ? (
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="error" sx={{ mb: 1, fontSize: '0.75rem' }}>
                     {loyaltyKpisError}
                  </Typography>
                  <Button variant="outlined" size="small" onClick={handleReloadLoyaltyKpis} sx={{ textTransform: 'none' }}>
                     Réessayer
                  </Button>
                </Box>
              ) : !loyaltyKpis ? (
                <Typography variant="body2" color="text.secondary">
                  Aucune donnée disponible
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                     <Typography variant="body2" sx={{ color: '#6B7280' }}>Points distribués</Typography>
                     <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1F2937' }}>
                        {numberFormatter.format(loyaltyKpis.loyalty?.points?.issuedInWindow || 0)} pts
                     </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                     <Typography variant="body2" sx={{ color: '#6B7280' }}>Récompenses actives</Typography>
                     <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1F2937' }}>
                        {numberFormatter.format(loyaltyKpis.loyalty?.rewards?.activeNow || 0)}
                     </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                     <Typography variant="body2" sx={{ color: '#6B7280' }}>Transactions traitées</Typography>
                     <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1F2937' }}>
                        {numberFormatter.format(loyaltyKpis.transactions?.ingestedInWindow || 0)}
                     </Typography>
                  </Box>
                   <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                     <Typography variant="body2" sx={{ color: '#6B7280' }}>Règles appliquées</Typography>
                     <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1F2937' }}>
                        {numberFormatter.format(loyaltyKpis.transactions?.rulesAppliedCountInWindow || 0)}
                     </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                     <Typography variant="body2" sx={{ color: '#6B7280' }}>Nouveaux clients (30j)</Typography>
                     <Typography
                        variant="subtitle2"
                        onClick={handleOpenLoyaltyCustomers}
                        sx={{
                          fontWeight: 600,
                          color: '#1F2937',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          textDecorationColor: '#CBD5E1',
                          '&:hover': { color: '#0F172A' },
                        }}
                      >
                        {numberFormatter.format(loyaltyKpis.customers?.newInWindow || 0)}
                     </Typography>
                  </Box>
                </Box>
              )}
            </Card>
          </Box>
        </Grid>
      </Grid>

      <Dialog
        open={loyaltyCustomersOpen}
        onClose={handleCloseLoyaltyCustomers}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Liste des contacts fidélité
        </DialogTitle>
        <DialogContent dividers>
          {loyaltyCustomersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : loyaltyCustomersError ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                {loyaltyCustomersError}
              </Typography>
              <Button variant="outlined" size="small" onClick={() => loadLoyaltyCustomers(loyaltyCustomersPage)}>
                Réessayer
              </Button>
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e5e7eb' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Profile ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Statut fidélité</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Points</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Créé le</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loyaltyCustomers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                        Aucun contact fidélité trouvé.
                      </TableCell>
                    </TableRow>
                  ) : (
                    loyaltyCustomers.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{row.profile_id || '—'}</TableCell>
                        <TableCell>{row.loyalty_status_name || row.loyalty_status || '—'}</TableCell>
                        <TableCell>{numberFormatter.format(row.points_balance || 0)}</TableCell>
                        <TableCell>{row.created_at ? new Date(row.created_at).toLocaleDateString('fr-FR') : '—'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
          <Typography variant="caption" sx={{ color: '#6B7280' }}>
            Total: {numberFormatter.format(loyaltyCustomersCount)}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleLoyaltyCustomersPrev} disabled={loyaltyCustomersLoading || loyaltyCustomersPage === 0}>
              Précédent
            </Button>
            <Button
              onClick={handleLoyaltyCustomersNext}
              disabled={loyaltyCustomersLoading || (loyaltyCustomersPage + 1) * loyaltyCustomersLimit >= loyaltyCustomersCount}
            >
              Suivant
            </Button>
            <Button onClick={handleCloseLoyaltyCustomers} variant="contained">
              Fermer
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
